import { useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, Smartphone, UserPlus } from 'lucide-react';
import { PASSWORD_MIN, requiresMfa, type Role } from '@cp/shared';
import { Banner, Button, Dialog, Field, Input, PasswordInput } from '@/components/ui';
import { RolePicker } from '@/components/users/RolePicker';
import { useCreateUser } from '@/hooks/useUsers';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';

export function CreateUserDialog({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<Role[]>(['viewer']);

  const create = useCreateUser({
    onSuccess: (user) => {
      toast.success(`Учётная запись ${user.login} создана`);
      setLogin('');
      setEmail('');
      setPassword('');
      setRoles(['viewer']);
      onClose();
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!create.isPending) create.mutate({ login, email, password, roles });
  };

  return (
    <Dialog
      open={open}
      title="Новый сотрудник"
      description="Учётная запись и роли"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
          <Button type="submit" form="create-user" variant="primary" icon={UserPlus} busy={create.isPending} disabled={roles.length === 0}>
            Создать
          </Button>
        </>
      }
    >
      <form id="create-user" className="stack" onSubmit={submit}>
        <Field label="Логин" htmlFor="new-login" hint="Латиница в нижнем регистре, цифры, точка и дефис">
          <Input
            id="new-login"
            value={login}
            onChange={(event) => setLogin(event.target.value.toLowerCase())}
            minLength={3}
            maxLength={60}
            autoComplete="off"
            required
          />
        </Field>

        <Field label="Почта" htmlFor="new-email">
          <Input id="new-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" required />
        </Field>

        <Field label="Пароль" htmlFor="new-password" hint={`Не короче ${PASSWORD_MIN} символов`}>
          <PasswordInput
            id="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={PASSWORD_MIN}
            autoComplete="new-password"
            required
          />
        </Field>

        <RolePicker value={roles} onChange={setRoles} />

        {/* Said before the account is created, so the first sign-in asking for
            a phone is expected rather than a surprise. */}
        {requiresMfa(roles) ? (
          <Banner tone="info" icon={Smartphone}>
            Для роли обязателен второй фактор: при первом входе потребуется приложение-аутентификатор.
          </Banner>
        ) : null}

        {create.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(create.error, 'Не удалось создать учётную запись')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
